import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { PageLayout } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { format, addDays, parseISO, isToday } from 'date-fns';
import { 
  ArrowLeft, CheckCircle2, Calendar, Trash2, AlertTriangle, Clock, 
  Archive, ChevronRight, Loader2, CalendarDays, X, Check
} from 'lucide-react';
import { useWorkAreas } from '../contexts/WorkAreaContext';

const API = '/api';

// Priority colours — CSS variables only, never hex. Feb 2026 token migration.
const PRIORITY_COLORS = {
  "-1": { bg: 'var(--orka-priority-emergency)', text: 'var(--orka-text-inverse)' },
  "0":  { bg: 'var(--orka-priority-fixed)',     text: 'var(--orka-text-inverse)' },
  "1":  { bg: 'var(--orka-priority-high)',      text: 'var(--orka-text-inverse)' },
  "2":  { bg: 'var(--orka-priority-medium)',    text: 'var(--orka-text-inverse)' },
  "3":  { bg: 'var(--orka-priority-low)',       text: 'var(--orka-text-inverse)' },
  "4":  { bg: 'var(--orka-priority-later)',     text: 'var(--orka-text-inverse)' },
  "5":  { bg: 'var(--orka-priority-someday)',   text: 'var(--orka-text-inverse)' },
};

const EFFORT_OPTIONS = [
  { value: 1, label: '<15m' },
  { value: 2, label: '15-30m' },
  { value: 3, label: '30-60m' },
  { value: 5, label: '1-3h' },
  { value: 8, label: '3-6h' },
  { value: 13, label: '6h+' },
];

const EODPage = () => {
  const { workAreaColors, workAreaName } = useWorkAreas();
  const WORK_AREA_COLORS = workAreaColors;
  const { user, logout, actingAs } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get date from URL or use today
  const dateParam = searchParams.get('date');
  const selectedDate = dateParam ? parseISO(dateParam) : new Date();
  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  
  // Footer inputs
  const [incompleteReason, setIncompleteReason] = useState('');
  const [reflectionNotes, setReflectionNotes] = useState('');

  // Fetch incomplete tasks for the selected date
  const fetchPendingTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      params.append('date', dateStr);
      
      const response = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (response.ok) {
        const allTasks = await response.json();
        // Filter to incomplete tasks only (To Do, In Progress, Blocked)
        const incompleteTasks = allTasks.filter(t => 
          ['To Do', 'In Progress', 'Blocked'].includes(t.status)
        );
        
        // Initialize task state with action tracking
        setTasks(incompleteTasks.map(t => ({
          ...t,
          action: null, // null = not handled, 'unschedule', 'reschedule', 'other'
          newDate: null,
          // For In Progress tasks that get rescheduled
          wasPartial: false,
          partialEffort: null
        })));
      }
    } catch (error) {
      console.error('Failed to fetch EOD tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [user, actingAs, dateStr]);

  useEffect(() => {
    fetchPendingTasks();
  }, [fetchPendingTasks]);

  // Generate next 5 days for quick reschedule
  const quickDates = useMemo(() => {
    const dates = [];
    for (let i = 1; i <= 5; i++) {
      const d = addDays(selectedDate, i);
      dates.push({
        date: format(d, 'yyyy-MM-dd'),
        label: format(d, 'EEE d/M')
      });
    }
    return dates;
  }, [selectedDate]);

  // Set task action
  const setTaskAction = (taskId, action, newDate = null) => {
    setTasks(prev => prev.map(t => {
      if (t.task_id !== taskId) return t;
      
      // Toggle off if same action clicked
      if (t.action === action && t.newDate === newDate) {
        return { ...t, action: null, newDate: null };
      }
      
      return { ...t, action, newDate };
    }));
  };

  // Set partial completion for In Progress tasks
  const setPartialCompletion = (taskId, wasPartial, partialEffort = null) => {
    setTasks(prev => prev.map(t => 
      t.task_id === taskId ? { ...t, wasPartial, partialEffort } : t
    ));
  };

  // Count handled tasks
  const handledCount = tasks.filter(t => t.action !== null).length;
  const totalCount = tasks.length;
  const allHandled = handledCount === totalCount && totalCount > 0;

  // Process EOD
  const processEOD = async () => {
    if (!allHandled) {
      toast.error('Please handle all tasks before saving');
      return;
    }

    setProcessing(true);
    
    const results = {
      rescheduled: 0,
      unscheduled: 0,
      continuations: 0,
      errors: 0
    };

    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);

      for (const task of tasks) {
        try {
          if (task.action === 'unschedule') {
            // Move to backlog
            await fetch(`${API}/tasks/${task.task_id}?${params}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ task_delivery_date: null, start_time: null, end_time: null })
            });
            results.unscheduled++;
            
          } else if (task.action === 'reschedule' || task.action === 'other') {
            // Check if In Progress and partially completed
            if (task.status === 'In Progress' && task.wasPartial && task.partialEffort) {
              // Update original task as Partial
              const isPersonal = task.work_area_id === 'Personal';
              await fetch(`${API}/tasks/${task.task_id}?${params}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ 
                  status: 'Partial',
                  [isPersonal ? 'task_actual_personal_effort' : 'task_actual_effort']: task.partialEffort
                })
              });
              
              // Create continuation task
              await fetch(`${API}/tasks?${params}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  title: `${task.title} (Continuation)`,
                  description: task.description || '',
                  work_area_id: task.work_area_id || 'General',
                  task_estimated_effort: task.task_estimated_effort || 3,
                  task_priority: task.task_priority ?? 3,
                  task_delivery_date: task.newDate,
                  status: 'To Do'
                })
              });
              results.continuations++;
              
            } else {
              // Simple reschedule
              await fetch(`${API}/tasks/${task.task_id}?${params}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ 
                  task_delivery_date: task.newDate, 
                  start_time: null, 
                  end_time: null,
                  task_reschedule_count: (task.task_reschedule_count || 0) + 1
                })
              });
            }
            results.rescheduled++;
          }
        } catch {
          results.errors++;
        }
      }

      // Show summary
      setSummaryData(results);
      setShowSummary(true);
      
    } catch (error) {
      toast.error('Failed to process EOD');
    } finally {
      setProcessing(false);
    }
  };

  // Task Row Component
  const TaskRow = ({ task }) => {
    const priorityColor = PRIORITY_COLORS[String(task.task_priority ?? 3)] || PRIORITY_COLORS["3"];
    const workAreaColor = WORK_AREA_COLORS[task.work_area_id] || WORK_AREA_COLORS['General'];
    const rescheduleWarning = task.task_reschedule_count >= 3 ? 'red' : task.task_reschedule_count >= 2 ? 'amber' : null;
    
    const showPartialSection = task.status === 'In Progress' && (task.action === 'reschedule' || task.action === 'other');

    return (
      <div className="border-b border-stone-100 last:border-b-0">
        {/* Main Row */}
        <div className="flex items-center gap-3 py-3 px-4">
          {/* Work Area Pill */}
          <span 
            className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
            style={{ backgroundColor: workAreaColor, color: '#333' }}
          >
            {workAreaName(task.work_area_id) || 'General'}
          </span>
          
          {/* Title */}
          <span className="flex-1 text-sm font-medium text-slate-800 truncate">
            {task.title}
          </span>
          
          {/* Status Badge */}
          <Badge 
            variant="secondary" 
            className={`text-xs ${
              task.status === 'Blocked' ? 'bg-red-100 text-red-700' :
              task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
              'bg-stone-100 text-stone-600'
            }`}
          >
            {task.status}
          </Badge>
          
          {/* Effort */}
          <span className="text-xs font-bold font-mono text-slate-600 w-6 text-center">
            {task.task_estimated_effort || 1}
          </span>
          
          {/* Reschedule Warning */}
          {rescheduleWarning && (
            <span className={`text-xs font-medium ${
              rescheduleWarning === 'red' ? 'text-red-600' : 'text-amber-600'
            }`}>
              Rescheduled {task.task_reschedule_count}x
            </span>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2 px-4 pb-3 flex-wrap">
          {/* Unschedule */}
          <button
            onClick={() => setTaskAction(task.task_id, 'unschedule')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              task.action === 'unschedule' 
                ? 'bg-amber-500 text-white' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid={`unschedule-${task.task_id}`}
          >
            <Archive className="w-3 h-3 inline mr-1" />
            Unschedule
          </button>
          
          {/* Quick Reschedule Pills */}
          {quickDates.map(qd => (
            <button
              key={qd.date}
              onClick={() => setTaskAction(task.task_id, 'reschedule', qd.date)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                task.action === 'reschedule' && task.newDate === qd.date
                  ? 'bg-indigo-500 text-white'
                  : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
              }`}
              data-testid={`reschedule-${task.task_id}-${qd.date}`}
            >
              {qd.label}
            </button>
          ))}
          
          {/* Other Date Picker */}
          <div className="flex items-center gap-1">
            <Input
              type="date"
              min={format(addDays(selectedDate, 1), 'yyyy-MM-dd')}
              value={task.action === 'other' ? task.newDate || '' : ''}
              onChange={(e) => setTaskAction(task.task_id, 'other', e.target.value)}
              className="h-8 w-36 text-xs"
              data-testid={`other-date-${task.task_id}`}
            />
          </div>
          
          {/* Result indicator */}
          {task.action && (
            <span className="text-xs text-slate-500 ml-2">
              → {task.action === 'unschedule' ? 'Backlog' : task.newDate}
            </span>
          )}
        </div>
        
        {/* Partial Completion Section for In Progress Tasks */}
        {showPartialSection && (
          <div className="mx-4 mb-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
            <p className="text-sm font-medium text-slate-700 mb-2">
              Was this task partially completed?
            </p>
            <RadioGroup 
              value={task.wasPartial ? 'yes' : 'no'} 
              onValueChange={(v) => setPartialCompletion(task.task_id, v === 'yes', null)}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id={`partial-no-${task.task_id}`} />
                <label htmlFor={`partial-no-${task.task_id}`} className="text-sm text-slate-600 cursor-pointer">
                  No, not started
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id={`partial-yes-${task.task_id}`} />
                <label htmlFor={`partial-yes-${task.task_id}`} className="text-sm text-slate-600 cursor-pointer">
                  Yes, partially done
                </label>
              </div>
            </RadioGroup>
            
            {task.wasPartial && (
              <div className="mt-3">
                <label className="text-xs font-medium text-slate-500 mb-1 block">
                  Effort completed:
                </label>
                <Select 
                  value={task.partialEffort ? String(task.partialEffort) : ''} 
                  onValueChange={(v) => setPartialCompletion(task.task_id, true, parseInt(v))}
                >
                  <SelectTrigger className="h-8 w-40" data-testid={`partial-effort-${task.task_id}`}>
                    <SelectValue placeholder="Select effort" />
                  </SelectTrigger>
                  <SelectContent>
                    {EFFORT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.value} - {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Summary Dialog
  const SummaryDialog = () => (
    <Dialog open={showSummary} onOpenChange={() => { setShowSummary(false); navigate('/dashboard'); }}>
      <DialogContent className="sm:max-w-lg" data-testid="eod-summary">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            End of Day Complete
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{summaryData?.rescheduled || 0}</p>
              <p className="text-xs text-green-700">Tasks Rescheduled</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{summaryData?.unscheduled || 0}</p>
              <p className="text-xs text-amber-700">Moved to Backlog</p>
            </div>
            {summaryData?.continuations > 0 && (
              <div className="bg-cyan-50 rounded-lg p-4 text-center col-span-2">
                <p className="text-2xl font-bold text-cyan-600">{summaryData.continuations}</p>
                <p className="text-xs text-cyan-700">Continuation Tasks Created</p>
              </div>
            )}
          </div>
          
          {/* Actions Summary */}
          <div className="text-sm text-slate-600 space-y-1">
            {summaryData?.rescheduled > 0 && (
              <p>• {summaryData.rescheduled} task{summaryData.rescheduled > 1 ? 's' : ''} rescheduled</p>
            )}
            {summaryData?.unscheduled > 0 && (
              <p>• {summaryData.unscheduled} task{summaryData.unscheduled > 1 ? 's' : ''} moved to backlog</p>
            )}
            {summaryData?.continuations > 0 && (
              <p>• {summaryData.continuations} continuation task{summaryData.continuations > 1 ? 's' : ''} created</p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button onClick={() => { setShowSummary(false); navigate('/dashboard'); }}>
            Return to Dashboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout user={user} logout={logout} theme={theme} setTheme={setTheme} actingAs={actingAs}>
      <div className="h-full flex flex-col bg-stone-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">End of Day</h1>
              <p className="text-sm text-white/80">
                {format(selectedDate, 'EEEE, d MMMM, yyyy')}
              </p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
              <h2 className="text-xl font-semibold text-slate-800">All tasks completed!</h2>
              <p className="text-slate-500 mt-2">Great work today. No incomplete tasks to process.</p>
              <Button className="mt-6" onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              {/* Task List */}
              <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden mb-6">
                {tasks.map(task => (
                  <TaskRow key={task.task_id} task={task} />
                ))}
              </div>
              
              {/* Optional Inputs */}
              <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-4 space-y-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Reason for incomplete tasks (optional)
                  </label>
                  <Input 
                    value={incompleteReason}
                    onChange={(e) => setIncompleteReason(e.target.value)}
                    placeholder="e.g., Blocked by dependencies, meetings ran over..."
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    Daily reflection notes (optional)
                  </label>
                  <Textarea 
                    value={reflectionNotes}
                    onChange={(e) => setReflectionNotes(e.target.value)}
                    placeholder="What went well? What could be improved?"
                    className="text-sm min-h-[80px] resize-none"
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-4 flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  <span className={`font-medium ${allHandled ? 'text-green-600' : 'text-amber-600'}`}>
                    {handledCount} / {totalCount}
                  </span>
                  {' '}tasks handled
                </div>
                
                <Button 
                  onClick={processEOD}
                  disabled={!allHandled || processing}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-eod"
                >
                  {processing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                  ) : (
                    <>Save Changes <ChevronRight className="h-4 w-4 ml-1" /></>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Summary Dialog */}
        <SummaryDialog />
      </div>
    </PageLayout>
  );
};

export default EODPage;
